import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ContentAttachments } from '@/components/widget/content-attachments';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import { ObligationBadge } from '@/components/widget/obligation-badge';
import { OutstandingBadge, RunningStickFigure, SittingStickFigure } from '@/components/widget/obligation-role-icons';
import { cn } from '@/lib/utils';
import {
  useContentUpdate,
  useDocumentRender,
  useMoveDocumentTableImage,
  useNavigateWithContentFlush,
} from '@/hooks';
import { useDocumentProposalStore } from '@/store/documentProposalStore';
import { toast } from 'sonner';
import { useDocumentScrollStore } from '@/store/documentScrollStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { toContentHref } from '@/utils/content-ref';
import { TemplateAddTableRowDialog } from './template-chrome/TemplateAddTableRowDialog';
import { TemplateEntriesPanel } from './template-chrome/TemplateEntriesPanel';
import { TemplateAddSectionFooter } from './template-chrome/TemplateAddSectionFooter';
import { TemplateDraftBanner } from './template-chrome/TemplateDraftBanner';
import { TemplateFillPanel } from './template-chrome/TemplateFillPanel';
import { TemplateInstanceStrip } from './template-chrome/TemplateInstanceStrip';
import { TemplateSectionChrome } from './template-chrome/TemplateSectionChrome';
import {
  Document,
  DocumentRender,
  DocumentRenderAttachmentSummary,
  DocumentRenderCitation,
  DocumentRenderMaintainer,
  DocumentRenderSection,
  DocumentRenderSectionTag,
} from '@vivport/backend/types';
import {
  CalendarDays,
  Check,
  Edit3,
  FileText,
  History,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Link as LinkIcon,
  Loader2,
  MessagesSquare,
  Mic,
  Network,
  Paperclip,
  Sparkles,
  UserPen,
  Video as VideoIcon,
  Workflow,
  X,
} from 'lucide-react';
import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppLink } from '@/components/ui/app-link';
import { renderMarkdown } from './document-markdown';
import { RevisionsPanel } from '@/components/revisions/RevisionsPanel';
import type { DocumentImageDragOptions } from './document-table-image-drag';
import { Notice } from '@/components/ui/notice';

interface DetailsDocumentProps {
  content: Document;
  isRegenerating: boolean;
}

const SCROLL_SUPPRESSION_MS = 450;

function getScrollContainer(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function LivingMaintainerBadge({ maintainer }: { maintainer: DocumentRenderMaintainer }) {
  const lastRun = maintainer.lastRun;
  const outcomeClass =
    lastRun?.status === 'FAILED'
      ? 'text-destructive'
      : lastRun?.status === 'SKIPPED'
        ? 'text-muted-foreground'
        : 'text-success';
  const summary =
    lastRun?.summaryLine && lastRun.summaryLine.length > 90
      ? `${lastRun.summaryLine.slice(0, 89)}…`
      : lastRun?.summaryLine;
  return (
    <div
      data-testid="living-maintainer-badge"
      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
    >
      <span className="inline-flex items-center gap-1 rounded-full border border-success-border/60 bg-success-subtle px-2 py-0.5 font-medium text-success">
        <Sparkles className="h-3 w-3" />
        Living
      </span>
      <AppLink
        to={`/work4me/processes/${encodeURIComponent(maintainer.processId)}`}
        className="font-medium text-foreground hover:underline"
      >
        {maintainer.name}
      </AppLink>
      <span>· {maintainer.triggerInWords}</span>
      {lastRun ? (
        <span className={outcomeClass} title={lastRun.summaryLine ?? undefined}>
          · last run {lastRun.status.toLowerCase()}
          {summary ? ` — ${summary}` : ''}
        </span>
      ) : null}
    </div>
  );
}

function headingClassesForDepth(depth: number): { tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5'; className: string } {
  if (depth === 0) {
    return { tag: 'h1', className: 'text-4xl font-medium tracking-tight md:text-5xl' };
  }
  if (depth === 1) {
    return { tag: 'h2', className: 'text-3xl font-medium tracking-tight' };
  }
  if (depth === 2) {
    return { tag: 'h3', className: 'text-2xl font-medium tracking-tight' };
  }
  if (depth === 3) {
    return { tag: 'h4', className: 'text-xl font-medium' };
  }
  return { tag: 'h5', className: 'text-lg font-medium' };
}

function SectionTags({ tags }: { tags: DocumentRenderSectionTag[] }) {
  if (tags.length === 0) return null;
  return (
    <span data-testid="section-tags" className="flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag.id}
          data-testid="section-tag"
          variant="outline"
          className="h-auto px-1.5 py-0.5 text-xs font-normal text-muted-foreground border-muted-foreground/30 bg-muted"
        >
          {tag.name}
        </Badge>
      ))}
    </span>
  );
}

function EditableSectionHeading({
  depth,
  outlineNumber,
  title,
  tags,
  disabled,
  onSave,
  proposal,
  onEditingChange,
  onHistory,
}: {
  depth: number;
  outlineNumber: string;
  title: string;
  tags: DocumentRenderSectionTag[];
  disabled: boolean;
  onSave: (next: string) => Promise<unknown>;
  proposal?: { title: string; onConsumed: () => void };
  onEditingChange?: (state: { isEditing: boolean; value: string } | null) => void;
  onHistory?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(
    () => () => {
      if (isEditingRef.current) onEditingChangeRef.current?.(null);
    },
    []
  );

  useEffect(() => {
    if (proposal === undefined) return;
    setEditValue(proposal.title);
    setIsEditing(true);
    proposal.onConsumed();
  }, [proposal]);

  const { tag, className } = headingClassesForDepth(depth);

  const startEdit = () => {
    if (disabled) return;
    setEditValue(title);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const commit = async () => {
    const trimmed = editValue.trim();
    if (trimmed === title) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save section title:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className={cn('mb-4 flex items-center gap-2', depth >= 3 && 'mb-2')}>
        {outlineNumber.length > 0 ? (
          <span className={cn('text-muted-foreground tabular-nums', className)}>{outlineNumber}</span>
        ) : null}
        <Input
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          onKeyDown={onKeyDown}
          className={cn('flex-1', className, 'h-auto py-1')}
          autoFocus
          disabled={isSaving}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => void commit()}
          disabled={isSaving || editValue.trim() === title}
          className="h-8 w-8 p-0"
          aria-label="Save title"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={cancelEdit}
          disabled={isSaving}
          className="h-8 w-8 p-0"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const Tag = tag;
  const displayTitle = title.length > 0 ? title : 'Untitled';

  return (
    <div
      className={cn(
        'group relative -ml-2 flex items-baseline gap-3 rounded-md px-2 py-1 transition-colors',
        !disabled && 'cursor-pointer hover:bg-muted/40',
        depth >= 3 ? 'mb-2' : 'mb-4',
      )}
      onClick={startEdit}
      role={disabled ? undefined : 'button'}
      tabIndex={disabled ? undefined : 0}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          startEdit();
        }
      }}
    >
      {outlineNumber.length > 0 ? (
        <span className={cn('text-muted-foreground tabular-nums', className)}>{outlineNumber}</span>
      ) : null}
      <Tag className={className}>{displayTitle}</Tag>
      <SectionTags tags={tags} />
      {!disabled ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            startEdit();
          }}
          className="ml-1 h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Edit title"
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      ) : null}
      {onHistory ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onHistory();
          }}
          className="h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label="History"
          title="History"
        >
          <History className="h-3 w-3" />
        </Button>
      ) : null}
    </div>
  );
}

function SourcesBlock({ citations }: { citations: DocumentRenderCitation[] }) {
  return (
    <div>
      <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Sources
      </p>
      <ol className="space-y-2">
        {citations.map((citation, index) => {
          const label = citation.title.length > 0 ? citation.title : citation.url;
          return (
            <li key={citation.id} className="text-sm text-foreground/80">
              <span className="mr-1 text-muted-foreground">[{index + 1}]</span>
              <span>{label}</span>
              <div className="ml-6 mt-0.5 text-xs">
                <a
                  href={citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-info hover:underline"
                >
                  {citation.url}
                </a>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function attachmentIcon(kind: DocumentRenderAttachmentSummary['contentType']): ReactNode {
  switch (kind) {
    case 'PHOTO':
      return <ImageIcon className="h-3.5 w-3.5" />;
    case 'RECORDING':
      return <Mic className="h-3.5 w-3.5" />;
    case 'VIDEO':
      return <VideoIcon className="h-3.5 w-3.5" />;
    case 'FILE':
      return <FileText className="h-3.5 w-3.5" />;
    case 'EVENT':
      return <CalendarDays className="h-3.5 w-3.5" />;
    case 'ANCHOR':
      return <Network className="h-3.5 w-3.5" />;
    case 'DOCUMENT':
      return <FileText className="h-3.5 w-3.5" />;
    case 'MESSAGE':
      return <MessagesSquare className="h-3.5 w-3.5" />;
    case 'NEO_CHAT':
      return <Sparkles className="h-3.5 w-3.5" />;
    case 'PROCESS_RUN':
      return <Workflow className="h-3.5 w-3.5" />;
    case 'MANUAL_SESSION':
      return <UserPen className="h-3.5 w-3.5" />;
    default:
      return <LinkIcon className="h-3.5 w-3.5" />;
  }
}

function AttachmentStrip({
  sectionId,
  sectionTitle,
  items,
  onOpen,
}: {
  sectionId: number;
  sectionTitle: string;
  items: DocumentRenderAttachmentSummary[];
  onOpen: (item: DocumentRenderAttachmentSummary) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleLimit = 4;
  const visible = items.slice(0, visibleLimit);
  const remaining = items.length - visible.length;
  const actioneeCount = items.reduce((sum, item) => sum + (item.obligationRollup?.actionee ?? 0), 0);
  const originatorCount = items.reduce((sum, item) => sum + (item.obligationRollup?.originator ?? 0), 0);
  const outstandingCount = items.reduce((sum, item) => sum + (item.obligationRollup?.outstanding ?? 0), 0);

  return (
    <div className="flex flex-col gap-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-foreground/80 transition-colors hover:bg-accent"
        >
          <Paperclip className="h-3.5 w-3.5" />
          {items.length}
          {actioneeCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <RunningStickFigure className="h-4 w-4" />
              {actioneeCount}
            </span>
          ) : null}
          {originatorCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <SittingStickFigure className="h-4 w-4" />
              {originatorCount}
            </span>
          ) : null}
          {outstandingCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <OutstandingBadge className="h-4 w-4" />
              {outstandingCount}
            </span>
          ) : null}
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {expanded
          ? null
          : visible.map((item) => (
              <span key={item.id} className="inline-flex items-center gap-1">
                <ObligationBadge contentId={item.id} rollup={item.obligationRollup} />
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="inline-flex max-w-[14rem] items-center gap-1.5 truncate rounded-full border bg-background px-2.5 py-1 text-foreground/80 transition-colors hover:bg-accent"
                  title={item.title || item.code}
                >
                  {attachmentIcon(item.contentType)}
                  <span className="truncate">{item.title || item.code}</span>
                </button>
              </span>
            ))}
        {!expanded && remaining > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-full border border-dashed px-2.5 py-1 text-muted-foreground hover:bg-accent"
          >
            +{remaining} more
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div className="text-sm text-foreground">
          <ContentAttachments
            parentContentId={sectionId}
            layout="embedded"
            title={sectionTitle}
            emptyText="No attachments"
            breadcrumbLabel={sectionTitle}
          />
        </div>
      ) : null}
    </div>
  );
}

function buildCodeToId(sections: DocumentRenderSection[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const section of sections) {
    map.set(section.code, section.id);
    map.set(String(section.id), section.id);
  }
  return map;
}

function pickTopMostSectionId(
  entries: IntersectionObserverEntry[],
  sectionRefs: Map<number, HTMLElement>,
): number | null {
  let bestId: number | null = null;
  let bestTop = Number.POSITIVE_INFINITY;

  for (const entry of entries) {
    if (!(entry.target instanceof HTMLElement)) continue;
    const idAttr = entry.target.dataset.sectionId;
    if (!idAttr) continue;
    const id = Number(idAttr);
    if (!Number.isFinite(id)) continue;
    if (!entry.isIntersecting) continue;
    const top = entry.boundingClientRect.top;
    if (top < bestTop) {
      bestTop = top;
      bestId = id;
    }
  }

  if (bestId !== null) return bestId;

  let fallbackId: number | null = null;
  let fallbackTop = -Number.POSITIVE_INFINITY;
  for (const [id, el] of sectionRefs.entries()) {
    const top = el.getBoundingClientRect().top;
    if (top <= 0 && top > fallbackTop) {
      fallbackTop = top;
      fallbackId = id;
    }
  }
  return fallbackId;
}

export function DetailsDocument({ content: document, isRegenerating }: DetailsDocumentProps) {
  const params = useParams<{ contentRef: string }>();
  const activeContentRef = String(params.contentRef ?? '').trim();

  const rootRef = document.hierarchyTree?.id ?? document.id;
  const renderQuery = useDocumentRender(rootRef);
  const updateMutation = useContentUpdate();
  const navigate = useNavigateWithContentFlush();

  const setStoreRoot = useDocumentScrollStore((s) => s.setRoot);
  const setStoreActiveSection = useDocumentScrollStore((s) => s.setActiveSection);
  const requestedScrollSectionId = useDocumentScrollStore((s) => s.requestedScrollSectionId);
  const requestedScrollNonce = useDocumentScrollStore((s) => s.requestedScrollNonce);
  const clearScrollRequest = useDocumentScrollStore((s) => s.clearScrollRequest);

  const renderData: DocumentRender | undefined = renderQuery.data;
  const sections = useMemo<DocumentRenderSection[]>(
    () => renderData?.sections ?? [],
    [renderData],
  );
  const rootSection = sections[0];
  const nonRootSections = useMemo(() => sections.slice(1), [sections]);

  const codeToId = useMemo(() => buildCodeToId(sections), [sections]);

  const requestScrollTo = useDocumentScrollStore((s) => s.requestScrollTo);
  const setMountedDocumentCode = useDocumentProposalStore((s) => s.setMountedDocumentCode);
  const pendingStoreProposal = useDocumentProposalStore((s) => s.pending);
  const consumeStoreProposal = useDocumentProposalStore((s) => s.consume);
  const rootCode = rootSection?.code ?? null;
  const [pendingTextProposal, setPendingTextProposal] = useState<{ sectionCode: string; text: string } | null>(null);
  const [historyFor, setHistoryFor] = useState<{ id: number; code: string; title: string } | null>(null);
  const [pendingTitleProposal, setPendingTitleProposal] = useState<{ sectionCode: string; title: string } | null>(null);

  useEffect(() => {
    setMountedDocumentCode(rootCode);
    return () => setMountedDocumentCode(null);
  }, [rootCode, setMountedDocumentCode]);

  useEffect(() => {
    if (pendingStoreProposal === null || rootCode === null || pendingStoreProposal.documentCode !== rootCode) return;
    const sectionId = codeToId.get(pendingStoreProposal.sectionCode);
    if (sectionId !== undefined) {
      setPendingTextProposal({ sectionCode: pendingStoreProposal.sectionCode, text: pendingStoreProposal.text });
      setPendingTitleProposal(
        pendingStoreProposal.title !== undefined
          ? { sectionCode: pendingStoreProposal.sectionCode, title: pendingStoreProposal.title }
          : null
      );
      requestScrollTo(sectionId);
    } else {
      toast.error('Neo suggested text for a section that is not on this page');
    }
    consumeStoreProposal(pendingStoreProposal.id);
  }, [pendingStoreProposal, rootCode, codeToId, requestScrollTo, consumeStoreProposal]);

  // A supersede re-renders a section under the same code with a new id; re-point the
  // active-section bookkeeping without waiting for the next scroll-spy tick.
  useEffect(() => {
    const currentId = activeSectionIdRef.current;
    if (currentId === null || sections.some((section) => section.id === currentId)) return;
    const code = useDocumentScrollStore.getState().activeSectionCode;
    if (code === null) return;
    const nextId = codeToId.get(code);
    if (nextId === undefined) return;
    activeSectionIdRef.current = nextId;
    setStoreActiveSection(nextId, code);
  }, [sections, codeToId, setStoreActiveSection]);

  const textProposal = useMemo(
    () =>
      pendingTextProposal === null
        ? null
        : {
            sectionCode: pendingTextProposal.sectionCode,
            proposal: {
              text: pendingTextProposal.text,
              ...(pendingTitleProposal?.sectionCode === pendingTextProposal.sectionCode
                ? { title: pendingTitleProposal.title }
                : {}),
              onConsumed: () => setPendingTextProposal(null),
            },
          },
    [pendingTextProposal, pendingTitleProposal]
  );
  const titleProposal = useMemo(
    () =>
      pendingTitleProposal === null
        ? null
        : {
            sectionCode: pendingTitleProposal.sectionCode,
            proposal: { title: pendingTitleProposal.title, onConsumed: () => setPendingTitleProposal(null) },
          },
    [pendingTitleProposal]
  );
  const textProposalFor = (sectionCode: string) =>
    textProposal?.sectionCode === sectionCode ? textProposal.proposal : undefined;
  const titleProposalFor = (sectionCode: string) =>
    titleProposal?.sectionCode === sectionCode ? titleProposal.proposal : undefined;

  const editingSectionRef = useRef<string | null>(null);
  const handleSectionEditing =
    (sectionCode: string) =>
    (state: { isEditing: boolean; value: string } | null): void => {
      if (state !== null && state.isEditing) {
        editingSectionRef.current = sectionCode;
        useViewContextStore.getState().setSlot('documentEditor', { sectionCode, unsavedText: state.value });
        return;
      }
      if (editingSectionRef.current === sectionCode) {
        editingSectionRef.current = null;
        useViewContextStore.getState().clearSlot('documentEditor');
      }
    };
  useEffect(() => () => useViewContextStore.getState().clearSlot('documentEditor'), []);

  const sectionRefs = useRef<Map<number, HTMLElement>>(new Map());
  const suppressSpyUntilRef = useRef<number>(0);
  const activeSectionIdRef = useRef<number | null>(null);
  const didInitialScrollRef = useRef<boolean>(false);
  const urlSyncTimerRef = useRef<number | null>(null);

  const fallbackActiveId = rootSection?.id ?? rootRef;
  const activeSectionId = codeToId.get(activeContentRef) ?? fallbackActiveId;

  useEffect(() => {
    setStoreRoot(rootRef);
  }, [rootRef, setStoreRoot]);

  const scheduleUrlSync = useCallback(
    (code: string) => {
      if (typeof window === 'undefined') return;
      if (urlSyncTimerRef.current !== null) {
        window.clearTimeout(urlSyncTimerRef.current);
      }
      urlSyncTimerRef.current = window.setTimeout(() => {
        const nextHref = toContentHref(code);
        if (window.location.pathname + window.location.search === nextHref) return;
        window.history.replaceState(window.history.state, '', nextHref);
      }, 600);
    },
    [],
  );

  const saveSection = useCallback(
    (sectionId: number, description: string) =>
      updateMutation.mutateAsync({
        id: sectionId,
        data: { contentType: 'DOCUMENT', description },
      }),
    [updateMutation],
  );

  const moveTableImageMutation = useMoveDocumentTableImage();
  const imageDragFor = useCallback(
    (sectionId: number): DocumentImageDragOptions => ({
      documentId: sectionId,
      disabled: isRegenerating,
      onMove: (input) => {
        moveTableImageMutation.mutate(input);
      },
    }),
    [isRegenerating, moveTableImageMutation],
  );

  const saveSectionTitle = useCallback(
    (sectionId: number, title: string) =>
      updateMutation.mutateAsync({
        id: sectionId,
        data: { contentType: 'DOCUMENT', title },
      }),
    [updateMutation],
  );

  useEffect(() => {
    if (sections.length === 0) return;
    if (didInitialScrollRef.current) return;
    const target = sectionRefs.current.get(activeSectionId);
    if (!target) return;
    const isRoot = rootSection !== undefined && activeSectionId === rootSection.id;
    if (!isRoot) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    activeSectionIdRef.current = activeSectionId;
    setStoreActiveSection(
      activeSectionId,
      sections.find((s) => s.id === activeSectionId)?.code ?? null,
    );
    didInitialScrollRef.current = true;
  }, [sections, activeSectionId, rootSection, setStoreActiveSection]);

  useEffect(() => {
    if (requestedScrollSectionId === null) return;
    const target = sectionRefs.current.get(requestedScrollSectionId);
    if (!target) {
      clearScrollRequest();
      return;
    }
    suppressSpyUntilRef.current = Date.now() + SCROLL_SUPPRESSION_MS;
    activeSectionIdRef.current = requestedScrollSectionId;
    setStoreActiveSection(
      requestedScrollSectionId,
      sections.find((s) => s.id === requestedScrollSectionId)?.code ?? null,
    );
    const isRoot = rootSection !== undefined && requestedScrollSectionId === rootSection.id;
    if (isRoot) {
      const scroller = getScrollContainer(target);
      if (scroller) {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    const section = sections.find((s) => s.id === requestedScrollSectionId);
    if (section) {
      scheduleUrlSync(section.code);
    }
    clearScrollRequest();
  }, [
    requestedScrollSectionId,
    requestedScrollNonce,
    sections,
    rootSection,
    scheduleUrlSync,
    setStoreActiveSection,
    clearScrollRequest,
  ]);

  useEffect(() => {
    if (sections.length === 0) return;
    const firstEl = nonRootSections.length > 0
      ? sectionRefs.current.get(nonRootSections[0].id)
      : sectionRefs.current.get(sections[0].id);
    const root = getScrollContainer(firstEl ?? null);

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressSpyUntilRef.current) return;
        const chosenId = pickTopMostSectionId(entries, sectionRefs.current);
        if (chosenId === null) return;
        if (chosenId === activeSectionIdRef.current) return;
        const section = sections.find((s) => s.id === chosenId);
        if (!section) return;
        activeSectionIdRef.current = chosenId;
        setStoreActiveSection(chosenId, section.code);
        scheduleUrlSync(section.code);
      },
      {
        root,
        rootMargin: '-72px 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    sectionRefs.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [sections, nonRootSections, scheduleUrlSync, setStoreActiveSection]);

  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && urlSyncTimerRef.current !== null) {
        window.clearTimeout(urlSyncTimerRef.current);
      }
    },
    [],
  );

  const handleAttachmentOpen = useCallback(
    (item: DocumentRenderAttachmentSummary) => {
      navigate(toContentHref(item.code));
    },
    [navigate],
  );

  if (renderQuery.isError) {
    return (
      <div data-testid="details-document" className="mx-auto max-w-3xl px-4 py-8">
        <Notice tone="destructive" className="p-4">
          Failed to load document. Please refresh to try again.
        </Notice>
      </div>
    );
  }

  if (renderQuery.isLoading || !rootSection) {
    return (
      <div data-testid="details-document" className="mx-auto max-w-3xl px-4 py-12">
        <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p className="text-xs uppercase tracking-[0.25em]">Loading document</p>
        </div>
      </div>
    );
  }

  const setSectionRef = (id: number) => (el: HTMLElement | null) => {
    if (el) {
      sectionRefs.current.set(id, el);
    } else {
      sectionRefs.current.delete(id);
    }
  };

  const renderSectionAttachments = (section: DocumentRenderSection): ReactNode => {
    const hasCitations = section.citations.length > 0;
    const hasOther = section.otherAttachments.length > 0;
    if (!hasCitations && !hasOther) {
      return (
        <div className="mt-6 border-y py-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            No Attachments
          </p>
        </div>
      );
    }
    return (
      <div className="mt-6 space-y-4 border-y py-4">
        {hasCitations ? <SourcesBlock citations={section.citations} /> : null}
        {hasOther ? (
          <AttachmentStrip
            sectionId={section.id}
            sectionTitle={section.title}
            items={section.otherAttachments}
            onOpen={handleAttachmentOpen}
          />
        ) : null}
      </div>
    );
  };

  const templateContext = renderData?.template ?? null;
  const isDefinition = templateContext?.role === 'definition';

  return (
    <article data-testid="details-document" className="px-[52px] pb-24">
      {templateContext?.role === 'instance' ? (
        <TemplateInstanceStrip
          template={templateContext}
          instanceRootId={rootSection.id}
          instanceTitle={rootSection.title}
        />
      ) : null}
      <header
        ref={setSectionRef(rootSection.id)}
        id={`section-${rootSection.id}`}
        data-section-id={rootSection.id}
        data-section-depth={rootSection.depth}
        data-active={rootSection.id === activeSectionId ? 'true' : undefined}
        className="scroll-mt-24 pb-2"
      >
        <EditableSectionHeading
          depth={0}
          outlineNumber=""
          title={rootSection.title}
          tags={rootSection.tags}
          disabled={isRegenerating}
          onSave={(value) => saveSectionTitle(rootSection.id, value)}
          proposal={titleProposalFor(rootSection.code)}
          onHistory={() => setHistoryFor({ id: rootSection.id, code: rootSection.code, title: rootSection.title })}
        />
        {renderData?.maintainer ? <LivingMaintainerBadge maintainer={renderData.maintainer} /> : null}
        <ContentDescriptionEditable
          description={rootSection.description}
          onSave={(value) => saveSection(rootSection.id, value)}
          onEditingChange={handleSectionEditing(rootSection.code)}
          proposal={textProposalFor(rootSection.code)}
          disabled={isRegenerating}
          placeholder="Click to add a lede or summary"
          maxLength={20000}
          renderContent={(content) => renderMarkdown(content, imageDragFor(rootSection.id))}
        />
        {renderSectionAttachments(rootSection)}
      </header>

      {isDefinition && templateContext !== null ? (
        <TemplateDraftBanner template={templateContext} definitionRootId={rootSection.id} />
      ) : null}

      {nonRootSections.map((section, index) => (
        <section
          key={section.code}
          ref={setSectionRef(section.id)}
          id={`section-${section.id}`}
          data-section-id={section.id}
          data-section-depth={section.depth}
          data-active={section.id === activeSectionId ? 'true' : undefined}
          className={cn(
            'scroll-mt-24',
            section.depth === 1 && 'mt-10',
            section.depth === 2 && 'mt-6',
            section.depth >= 3 && 'mt-5',
          )}
        >
          <EditableSectionHeading
            depth={section.depth}
            outlineNumber={section.outlineNumber}
            title={section.title}
            tags={section.tags}
            disabled={isRegenerating}
            onSave={(value) => saveSectionTitle(section.id, value)}
            proposal={titleProposalFor(section.code)}
            onHistory={() => setHistoryFor({ id: section.id, code: section.code, title: section.title })}
          />
          <ContentDescriptionEditable
            description={section.description}
            onSave={(value) => saveSection(section.id, value)}
            onEditingChange={handleSectionEditing(section.code)}
            proposal={textProposalFor(section.code)}
            disabled={isRegenerating}
            placeholder="Click to add description"
            maxLength={20000}
            renderContent={(content) => renderMarkdown(content, imageDragFor(section.id))}
          />
          {isDefinition && templateContext !== null ? (
            <TemplateSectionChrome
              section={section}
              template={templateContext}
              isFirstSection={index === 0}
            />
          ) : null}
          {templateContext?.role === 'instance' && section.templateSection !== null ? (
            <>
              <TemplateFillPanel section={section} instanceRootId={rootSection.id} />
              <TemplateAddTableRowDialog
                section={section}
                instanceRootId={rootSection.id}
                rootPhotos={rootSection.otherAttachments}
              />
              <TemplateEntriesPanel
                section={section}
                instanceRootId={rootSection.id}
                rootPhotos={rootSection.otherAttachments}
              />
            </>
          ) : null}
          {renderSectionAttachments(section)}
        </section>
      ))}

      {isDefinition && templateContext !== null && templateContext.status === 'DRAFT' ? (
        <TemplateAddSectionFooter template={templateContext} definitionRootId={rootSection.id} />
      ) : null}
      {historyFor !== null ? (
        <RevisionsPanel
          contentId={historyFor.id}
          code={historyFor.code}
          title={historyFor.title}
          open={true}
          onOpenChange={(open) => {
            if (!open) setHistoryFor(null);
          }}
        />
      ) : null}
    </article>
  );
}
